import { useState, useMemo, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { useLocation } from 'react-router-dom';
import {
  useParameters,
  useCreateParameter,
  useBulkCreateParameters,
  useUpdateParameter,
  useDeleteParameter,
  useLayers,
} from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { UnsavedBar } from '@/shared/ui/UnsavedBar';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import { BulkUploadModal } from '@/shared/ui/BulkUploadModal';
import { ModalShell } from '@/shared/ui/ModalShell';
import { FormField, fieldInputStyle } from '@/shared/ui/FormField';
import { SearchInput } from '@/shared/ui/SearchInput';
import { AppSelect } from '@/shared/ui/AppSelect';
import { paramTypeMeta, semantic, typography, radius } from '@/shared/theme/tokens';

const TYPE_OPTIONS = ['target', 'controllable', 'monitored', 'observed'];

// Distinct layer badge colors cycling through a palette
const LAYER_PALETTE = [
  { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.25)', text: '#6366f1' },
  { bg: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.25)', text: '#0f9488' },
  { bg: 'rgba(236,72,153,0.1)', border: 'rgba(236,72,153,0.25)', text: '#db2777' },
  { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)', text: '#d97706' },
  { bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)', text: '#2563eb' },
];

function LayerBadge({ name, index }) {
  const style = LAYER_PALETTE[index % LAYER_PALETTE.length];
  return (
    <span title={name} style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '2px 7px', borderRadius: radius.sm,
      background: style.bg, border: `1px solid ${style.border}`,
      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: style.text, whiteSpace: 'nowrap',
      maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 10, fontVariationSettings: "'FILL' 1", flexShrink: 0 }}>layers</span>
      {name}
    </span>
  );
}


// Derive layer names for a parameter from both sources:
// 1. p.layers (param → layer IDs, if BE populates it)
// 2. layers[].parameterIds (layer → param IDs, always populated)
function getParamLayerNames(paramId, layers, paramLayerIds) {
  const fromLayers = layers.filter((l) => l.parameterIds?.includes(paramId)).map((l) => l.name);
  if (fromLayers.length) return fromLayers.join(', ');
  if (paramLayerIds?.length) {
    const byId = Object.fromEntries(layers.map((l) => [l.id, l.name]));
    const fromIds = paramLayerIds.map((id) => byId[id]).filter(Boolean);
    if (fromIds.length) return fromIds.join(', ');
  }
  return '';
}

// ── Main ParametersTab ────────────────────────────────────────────────────────
export function ParametersTab({ deploymentId, canManage }) {
  const toast = useToast();
  const toastApiError = useApiErrorToast();
  const c = useThemeColors();
  const isDark = useIsDark();
  const { search } = useLocation();
  const isDevMode = new URLSearchParams(search).has('dev');

  const { data: parameters = [], isLoading } = useParameters(deploymentId);
  const { data: layers = [] } = useLayers(deploymentId);
  const layerById = useMemo(() => Object.fromEntries(layers.map((l) => [l.id, l.name])), [layers]);
  const bulkCreateParam = useBulkCreateParameters(deploymentId);
  const createParam = useCreateParameter(deploymentId);
  const updateParam = useUpdateParameter(deploymentId);
  const deleteParam = useDeleteParameter(deploymentId);
  const paramModalPending = createParam.isPending || updateParam.isPending;

  // Single owner of param create/update mutations; the modal is a controlled
  // form that delegates here via onSubmit (avoids a second useUpdateParameter
  // instance racing on cache invalidation).
  const submitParamModal = useCallback(
    async ({ mode, paramId, payload }) => {
      if (mode === 'create') {
        await createParam.mutateAsync(payload);
      } else {
        await updateParam.mutateAsync({ paramId, payload });
      }
    },
    [createParam, updateParam]
  );

  // Local pending edits: map paramId → partial patch
  const [pendingEdits, setPendingEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const hasUnsaved = Object.keys(pendingEdits).length > 0;

  const [paramModal, setParamModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLayerId, setFilterLayerId] = useState('');

  async function handleDownload() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Parameters');
    ws.columns = [
      { header: 'displayName', key: 'displayName' },
      { header: 'dataKey', key: 'dataKey' },
      { header: 'type', key: 'type' },
      { header: 'layers', key: 'layers' },
      { header: 'integrationKey', key: 'integrationKey' },
      { header: 'unit', key: 'unit' },
      { header: 'optimumLow', key: 'optimumLow' },
      { header: 'optimumHigh', key: 'optimumHigh' },
      { header: 'criticalLow', key: 'criticalLow' },
      { header: 'criticalHigh', key: 'criticalHigh' },
      { header: 'stalenessThreshold', key: 'stalenessThreshold' },
    ];
    // Export only the currently-filtered rows, not the full set (AUT-125).
    displayParams.forEach((p) => ws.addRow({
      displayName: p.displayName ?? '',
      dataKey: p.dataKey ?? '',
      type: p.type ?? '',
      layers: getParamLayerNames(p.id, layers, p.layers),
      integrationKey: p.integrationKey ?? '',
      unit: p.unit ?? '',
      optimumLow: p.optimumLow ?? '',
      optimumHigh: p.optimumHigh ?? '',
      criticalLow: p.criticalLow ?? '',
      criticalHigh: p.criticalHigh ?? '',
      stalenessThreshold: p.stalenessThreshold ?? '',
    }));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `parameters-${deploymentId}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  }

  const filtered = useMemo(
    () => parameters.filter((p) => {
      if (searchQ && !p.displayName?.toLowerCase().includes(searchQ.toLowerCase()) && !p.dataKey?.toLowerCase().includes(searchQ.toLowerCase())) return false;
      if (filterType && p.type !== filterType) return false;
      if (filterLayerId) {
        // Layer membership lives on layer.parameterIds (BE doesn't populate p.layers),
        // so match via reverse lookup, falling back to p.layers when present (AUT-125).
        const layer = layers.find((l) => String(l.id) === String(filterLayerId));
        const inLayer = layer?.parameterIds?.includes(p.id)
          || p.layers?.map(String).includes(String(filterLayerId));
        if (!inLayer) return false;
      }
      return true;
    }),
    [parameters, searchQ, filterType, filterLayerId, layers]
  );

  // Merge pending into display data
  const displayParams = useMemo(
    () => filtered.map((p) => pendingEdits[p.id] ? { ...p, ...pendingEdits[p.id] } : p),
    [filtered, pendingEdits]
  );

  const handleFieldChange = useCallback((paramId, field, value) => {
    setPendingEdits((prev) => ({
      ...prev,
      [paramId]: { ...(prev[paramId] ?? {}), [field]: value },
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const original = Object.fromEntries(parameters.map((p) => [p.id, p]));
      await Promise.all(
        Object.entries(pendingEdits).map(([paramId, patch]) =>
          updateParam.mutateAsync({ paramId, payload: { ...original[paramId], ...patch } })
        )
      );
      setPendingEdits({});
      toast({ message: 'Parameter updated', type: 'success' });
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  }, [pendingEdits, parameters, updateParam, toast, toastApiError]);

  const handleDiscard = useCallback(() => setPendingEdits({}), []);

  async function handleDelete(param) {
    try {
      await deleteParam.mutateAsync(param.id);
      setDeleteTarget(null);
      toast({ message: 'Parameter deleted', type: 'success' });
    } catch (err) {
      toastApiError(err);
    }
  }

  if (isLoading) return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: '16px' }}>
      {[1, 2, 3].map((i) => <SkeletonCard key={i} rows={5} height={200} />)}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Dev mode banner */}
      {isDevMode && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 16px', borderRadius: radius.lg, marginBottom: '14px',
          background: semantic.warningBg, border: `1px solid ${semantic.warningBorder}`,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: semantic.warning }}>developer_mode</span>
          <span style={{ fontSize: typography.fontSize.base, color: semantic.warning, fontWeight: typography.fontWeight.semibold }}>
            Dev mode — dataKey fields are editable
          </span>
        </div>
      )}

      {hasUnsaved && (
        <UnsavedBar count={Object.keys(pendingEdits).length} onSave={handleSave} onDiscard={handleDiscard} saving={saving} />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <SearchInput
          value={searchQ}
          onChange={setSearchQ}
          placeholder="Search parameters…"
          size="sm"
          containerStyle={{ minWidth: 200 }}
        />
        <div style={{ width: 170, flexShrink: 0 }}>
          <AppSelect
            value={filterType || null}
            onChange={(v) => setFilterType(v ?? '')}
            placeholder="All Types"
            options={Object.entries(paramTypeMeta).map(([k, v]) => ({ value: k, label: v.label }))}
            size="sm"
            searchable={false}
            clearable
            ariaLabel="Filter by type"
          />
        </div>
        <div style={{ width: 170, flexShrink: 0 }}>
          <AppSelect
            value={filterLayerId || null}
            onChange={(v) => setFilterLayerId(v ?? '')}
            placeholder="All Layers"
            options={layers.map((l) => ({ value: String(l.id), label: l.name }))}
            size="sm"
            searchable={false}
            clearable
            ariaLabel="Filter by layer"
          />
        </div>
        <PrimaryButton variant="secondary" icon="download" size="sm" onClick={handleDownload}>
          Download
        </PrimaryButton>
        {canManage && (
          <>
            <PrimaryButton variant="secondary" icon="upload_file" size="sm" onClick={() => setBulkOpen(true)}>
              Bulk Upload
            </PrimaryButton>
            <PrimaryButton icon="add" size="sm" onClick={() => setParamModal({ mode: 'create' })}>
              Add Parameter
            </PrimaryButton>
          </>
        )}
      </div>

      {/* Parameter Cards — fixed 3-column grid */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {displayParams.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: c.textSubtle }}>
          <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: '8px', opacity: 0.4 }}>tune</span>
          <p style={{ margin: 0, fontSize: typography.fontSize.md }}>No parameters configured yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: '14px' }}>
          {displayParams.map((param) => {
            const isDirty = !!pendingEdits[param.id];
            return (
              <ParameterCard
                key={param.id}
                param={param}
                isDirty={isDirty}
                isDevMode={isDevMode}
                canManage={canManage}
                layerById={layerById}
                layers={layers}
                isDark={isDark}
                onFieldChange={(field, value) => handleFieldChange(param.id, field, value)}
                onEdit={() => setParamModal({ mode: 'edit', param })}
                onDelete={() => setDeleteTarget(param)}
              />
            );
          })}
        </div>
      )}
      </div>

      {/* Modals */}
      {paramModal && (
        <ParameterFormModal
          mode={paramModal.mode}
          param={paramModal.param}
          isDevMode={isDevMode}
          pending={paramModalPending}
          onSubmit={submitParamModal}
          onClose={() => setParamModal(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          name={deleteTarget.displayName}
          loading={deleteParam.isPending}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {bulkOpen && (
        <BulkUploadModal
          title="Bulk Upload Parameters"
          subtitle="Upload a CSV file to add multiple parameters"
          entityLabel="Parameter"
          requiredColumns={['displayName', 'dataKey', 'type']}
          filterRow={(r) => !!(r.displayName || r.dataKey)}
          previewColumns={[
            { key: 'displayName',       label: 'Display Name', flex: '1.5fr' },
            { key: 'dataKey',           label: 'Data Key',     flex: '1.5fr' },
            { key: 'type',              label: 'Type',         flex: '1fr' },
            { key: 'unit',              label: 'Unit',         flex: '0.6fr' },
            { key: 'optimumLow',        label: 'Opt Low',      flex: '0.7fr' },
            { key: 'optimumHigh',       label: 'Opt High',     flex: '0.7fr' },
            { key: 'criticalLow',       label: 'Crit Low',     flex: '0.7fr' },
            { key: 'criticalHigh',      label: 'Crit High',    flex: '0.7fr' },
            { key: 'stalenessThreshold', label: 'Stale (min)', flex: '0.8fr' },
          ]}
          sampleCsv={'displayName,dataKey,type,integrationKey,unit,optimumLow,optimumHigh,criticalLow,criticalHigh,stalenessThreshold\nFurnace Temperature,furnace_temp_c,target,,°C,850,950,800,1000,5\nCoal Feed Rate,coal_feed_rate,controllable,,kg/hr,120,180,100,210,10\n'}
          sampleFilename="sample-parameters.csv"
          mutateAsync={(file) => bulkCreateParam.mutateAsync(file)}
          isPending={bulkCreateParam.isPending}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ── ParameterCard ─────────────────────────────────────────────────────────────
function ParameterCard({ param, isDirty, isDevMode, canManage, layerById: _layerById = {}, layers = [], onFieldChange: _onFieldChange, onEdit, onDelete }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [hov, setHov] = useState(false);
  const tm = paramTypeMeta[param.type] ?? paramTypeMeta.target;
  return (
    <div
      style={{
        background: c.bg,
        border: isDirty
          ? `1.5px solid ${semantic.warningBorder}`
          : `1px solid ${hov ? 'rgba(62,181,231,0.25)' : c.border}`,
        borderRadius: radius['2xl'],
        overflow: 'hidden',
        minWidth: 0,
        display: 'flex', flexDirection: 'column', height: '100%',
        boxShadow: hov
          ? (isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 6px 20px rgba(30,67,150,0.10)')
          : (isDark ? 'none' : '0 2px 6px rgba(30,67,150,0.05)'),
        transition: 'all 0.18s',
        transform: hov ? 'translateY(-1px)' : 'none',
        position: 'relative',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* Dirty dot indicator */}
      {isDirty && (
        <div style={{
          position: 'absolute', top: 10, right: 10, width: 7, height: 7,
          borderRadius: '50%', background: semantic.warning,
          boxShadow: `0 0 0 2px ${semantic.warningBg}`,
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Accent bar */}
      <div style={{ height: '3px', background: `linear-gradient(90deg, ${tm.text}, rgba(62,181,231,0.6))` }} />

      {/* Header */}
      <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${c.border}` }}>
        {/* Title row: name + action buttons */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
          <h4 style={{ margin: 0, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading, letterSpacing: typography.letterSpacing.normal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: isDirty ? '16px' : 0, minWidth: 0, flex: 1 }}>
            {param.displayName}
          </h4>
          {canManage && (
            <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
              <button onClick={onEdit} style={cardBtnStyle(c)} title="Edit">
                <span className="material-symbols-outlined" style={{ fontSize: 13, color: c.textSubtle }}>edit</span>
              </button>
              <button onClick={onDelete} style={{ ...cardBtnStyle(c), borderColor: semantic.errorBorder }} title="Delete">
                <span className="material-symbols-outlined" style={{ fontSize: 13, color: semantic.error }}>delete</span>
              </button>
            </div>
          )}
        </div>
        {/* Type badge + stale badge + layer badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          <span style={{
            padding: '2px 7px', borderRadius: radius.sm,
            background: tm.bg, border: `1px solid ${tm.border}`,
            fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: tm.text, letterSpacing: typography.letterSpacing.wider,
          }}>
            {tm.label.toUpperCase()}
          </span>
          {layers.filter((l) => l.parameterIds?.includes(param.id) || param.layers?.includes(l.id))
            .map((l, i) => <LayerBadge key={l.id} name={l.name} index={i} />)}
        </div>
      </div>

      {/* Body — flex column so the Data Key block can pin to the bottom and align
          across cards regardless of how many layer chips push the header taller. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '12px 16px' }}>
      {/* Fields grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px 14px' }}>
        <ParamField label="Integration Key" value={param.integrationKey} truncate />
        <ParamField label="Unit" value={param.unit} align="right" />
        <ParamField label="Optimal Range" value={(() => {
          const lo = param.optimumLow != null && param.optimumLow !== '' ? param.optimumLow : null;
          const hi = param.optimumHigh != null && param.optimumHigh !== '' ? param.optimumHigh : null;
          if (lo != null && hi != null) return `${lo} – ${hi}`;
          if (lo != null) return `≥ ${lo}`;
          if (hi != null) return `≤ ${hi}`;
          return null;
        })()} />
        <ParamField label="Critical Range" value={(() => {
          const lo = param.criticalLow != null && param.criticalLow !== '' ? param.criticalLow : null;
          const hi = param.criticalHigh != null && param.criticalHigh !== '' ? param.criticalHigh : null;
          if (lo != null && hi != null) return `${lo} – ${hi}`;
          if (lo != null) return `≥ ${lo}`;
          if (hi != null) return `≤ ${hi}`;
          return null;
        })()} valueColor="#dc2626" />
        <ParamField label="Stale After" value={param.stalenessThreshold != null ? `${param.stalenessThreshold} min` : null} align="right" />
      </div>

      {/* dataKey — pinned to the bottom so it aligns across cards of varying header height */}
      <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
        <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          Data Key
          {!isDevMode && <span className="material-symbols-outlined" style={{ fontSize: 11, color: c.textSubtle }}>lock</span>}
          {isDevMode && <span style={{ fontSize: typography.fontSize.xs, color: semantic.warning, fontWeight: typography.fontWeight.semibold, background: semantic.warningBg, padding: '1px 5px', borderRadius: radius.xs }}>EDITABLE</span>}
        </div>
        <div style={{
          padding: '5px 9px', borderRadius: radius.sm,
          background: isDevMode ? semantic.warningBg : (isDark ? 'rgba(255,255,255,0.04)' : c.bgMuted),
          border: isDevMode ? `1px solid ${semantic.warningBorder}` : `1px solid ${c.border}`,
          fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm, color: c.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {param.dataKey || '—'}
        </div>
      </div>
      </div>
    </div>
  );
}

function ParamField({ label, value, truncate = false, align = 'left', stale = false, valueColor }) {
  const c = useThemeColors();
  return (
    <div style={{ minWidth: 0, textAlign: align }}>
      <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: stale ? '#d97706' : c.textSubtle, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', marginBottom: '2px', opacity: stale ? 0.7 : 1 }}>{label}</div>
      <div
        title={truncate && value ? value : undefined}
        style={{
          fontSize: typography.fontSize.base, color: valueColor ?? (stale ? c.textSubtle : c.textPrimary), fontWeight: typography.fontWeight.medium,
          opacity: stale ? 0.6 : 1,
          ...(truncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
        }}
      >{value ?? '—'}</div>
    </div>
  );
}

// ── ParameterFormModal ────────────────────────────────────────────────────────
function ParameterFormModal({ mode, param, isDevMode, pending, onSubmit, onClose }) {
  const c = useThemeColors();
  const toast = useToast();
  const toastApiError = useApiErrorToast();

  const [form, setForm] = useState({
    displayName: param?.displayName ?? '',
    dataKey: param?.dataKey ?? '',
    integrationKey: param?.integrationKey ?? '',
    unit: param?.unit ?? '',
    optimumLow: param?.optimumLow ?? '',
    optimumHigh: param?.optimumHigh ?? '',
    criticalLow: param?.criticalLow ?? '',
    criticalHigh: param?.criticalHigh ?? '',
    stalenessThreshold: param?.stalenessThreshold ?? '',
    type: param?.type ?? 'target',
  });
  const [errors, setErrors] = useState({});

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.displayName.trim()) e.displayName = 'Required';
    if (!form.dataKey.trim()) e.dataKey = 'Required';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const payload = {
      displayName: form.displayName,
      dataKey: form.dataKey,
      type: form.type,
      integrationKey: form.integrationKey,
      unit: form.unit,
      optimumLow: form.optimumLow !== '' ? Number(form.optimumLow) : undefined,
      optimumHigh: form.optimumHigh !== '' ? Number(form.optimumHigh) : undefined,
      criticalLow: form.criticalLow !== '' ? Number(form.criticalLow) : undefined,
      criticalHigh: form.criticalHigh !== '' ? Number(form.criticalHigh) : undefined,
      stalenessThreshold: form.stalenessThreshold !== '' ? Number(form.stalenessThreshold) : undefined,
    };
    try {
      await onSubmit({ mode, paramId: param?.id, payload });
      toast({ message: mode === 'create' ? 'Parameter added' : 'Parameter updated', type: 'success' });
      onClose();
    } catch (err) {
      toastApiError(err);
    }
  }

  const dataKeyLocked = !isDevMode && mode === 'edit';

  return (
    <ModalShell
      onClose={onClose}
      icon="tune"
      iconVariant="primary"
      title={mode === 'create' ? 'Add Parameter' : 'Edit Parameter'}
      subtitle={mode === 'create' ? 'Define a new process parameter' : 'Update parameter configuration'}
      maxWidth="520px"
      footer={
        <>
          <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>Cancel</PrimaryButton>
          <PrimaryButton type="submit" form="param-form" disabled={pending} icon={mode === 'create' ? 'add' : 'check'}>
            {pending ? 'Saving…' : mode === 'create' ? 'Add Parameter' : 'Save Changes'}
          </PrimaryButton>
        </>
      }
    >
      <form id="param-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField label="Display Name" error={errors.displayName} required>
              <input value={form.displayName} onChange={(e) => setField('displayName', e.target.value)} placeholder="e.g. Furnace Temperature" style={fieldInputStyle(c, !!errors.displayName)} />
            </FormField>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <FormField label="Data Key" error={errors.dataKey} required hint={dataKeyLocked ? 'Read-only. Append ?dev=true to URL to edit.' : undefined}>
              <input
                value={form.dataKey}
                onChange={(e) => setField('dataKey', e.target.value)}
                placeholder="e.g. furnace_temp_c"
                readOnly={dataKeyLocked}
                style={{
                  ...fieldInputStyle(c, !!errors.dataKey),
                  background: dataKeyLocked ? c.bgMuted : c.bgSubtle,
                  cursor: dataKeyLocked ? 'not-allowed' : 'text',
                  color: dataKeyLocked ? c.textSubtle : c.textPrimary,
                  fontFamily: typography.fontFamily.mono,
                }}
              />
            </FormField>
          </div>

          <FormField label="Integration Key">
            <input value={form.integrationKey} onChange={(e) => setField('integrationKey', e.target.value)} placeholder="e.g. FT_001" style={fieldInputStyle(c, false)} />
          </FormField>
          <FormField label="Unit">
            <input value={form.unit} onChange={(e) => setField('unit', e.target.value)} placeholder="e.g. °C" style={fieldInputStyle(c, false)} />
          </FormField>
          <FormField label="Optimum Low">
            <input type="number" value={form.optimumLow} onChange={(e) => setField('optimumLow', e.target.value)} placeholder="0" style={fieldInputStyle(c, false)} />
          </FormField>
          <FormField label="Optimum High">
            <input type="number" value={form.optimumHigh} onChange={(e) => setField('optimumHigh', e.target.value)} placeholder="100" style={fieldInputStyle(c, false)} />
          </FormField>
          <FormField label="Critical Low" hint="Broader limit — triggers critical alert">
            <input type="number" value={form.criticalLow} onChange={(e) => setField('criticalLow', e.target.value)} placeholder="e.g. 800" style={{ ...fieldInputStyle(c, false), borderColor: form.criticalLow !== '' ? 'rgba(220,38,38,0.4)' : undefined }} />
          </FormField>
          <FormField label="Critical High" hint="Broader limit — triggers critical alert">
            <input type="number" value={form.criticalHigh} onChange={(e) => setField('criticalHigh', e.target.value)} placeholder="e.g. 1000" style={{ ...fieldInputStyle(c, false), borderColor: form.criticalHigh !== '' ? 'rgba(220,38,38,0.4)' : undefined }} />
          </FormField>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField label="Staleness Threshold (min)" hint="Mark stale if no reading received within this window">
              <input type="number" value={form.stalenessThreshold} onChange={(e) => setField('stalenessThreshold', e.target.value)} placeholder="e.g. 5" style={fieldInputStyle(c, false)} />
            </FormField>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField label="Type">
              <AppSelect
                value={form.type}
                onChange={(v) => setField('type', v ?? 'target')}
                options={TYPE_OPTIONS.map((t) => ({ value: t, label: paramTypeMeta[t].label }))}
                size="sm"
                searchable={false}
                ariaLabel="Parameter type"
              />
            </FormField>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}


function ConfirmDeleteModal({ name, loading, onConfirm, onCancel }) {
  const c = useThemeColors();
  return (
    <ModalShell
      onClose={onCancel}
      icon="delete"
      iconVariant="danger"
      title="Delete Parameter"
      maxWidth="360px"
      footer={
        <>
          <PrimaryButton variant="secondary" onClick={onCancel} disabled={loading}>Cancel</PrimaryButton>
          <PrimaryButton variant="danger" onClick={onConfirm} disabled={loading} icon="delete">
            {loading ? 'Deleting…' : 'Delete'}
          </PrimaryButton>
        </>
      }
    >
      <p style={{ fontSize: typography.fontSize.md, color: c.textLabel, margin: 0, lineHeight: 1.5 }}>
        Delete <strong>{name}</strong>? This cannot be undone.
      </p>
    </ModalShell>
  );
}

function cardBtnStyle(c) {
  return {
    width: 26, height: 26, borderRadius: '6px',
    background: 'transparent', border: `1px solid ${c.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0,
  };
}

